import {
  AddEmployeesRequest as AddEmployeesRequestDto,
  AddGroupRequest as AddGroupRequestDto,
  BulkOrganizationApplicationCreationRequest as BulkOrganizationApplicationCreationRequestDto,
  BulkOrganizationApplicationCreationResponse as BulkOrganizationApplicationCreationResponseDto,
  ChampionWithRank as ChampionWithRankDto,
  CoreEntityDomainsOrganizationsEntrypointsApiV1SchemasResponseHRISIntegration as HRISIntegrationDto,
  CoreEntityDomainsOrganizationsEntrypointsApiV1SchemasResponseOrganization as OrganizationDto,
  DeviceMdmActionsResponse as DeviceMdmActionsResponseDto,
  Employee as EmployeeDto,
  EmployeeDirectoryListing as EmployeeDirectoryListingDto,
  EmployeeStatusEnum,
  Entitlement as EntitlementDto,
  EntitlementSlugEnum,
  GetOrgAppChampsBulkResponse as GetOrgAppChampsBulkResponseDto,
  Group as GroupDto,
  IntakeForm as IntakeFormDto,
  MicrosoftTenantResponse as MicrosoftTenantResponseDto,
  NewMicrosoftTenantRequest as NewMicrosoftTenantRequestDto,
  OrganizationAdditionalInfo as OrganizationAdditionalInfoDto,
  OrganizationAddOnMapping as OrganizationAddOnDto,
  OrganizationApplication as OrganizationApplicationDto,
  OrganizationApplicationCreationRequest as AddOrganizationApplicationRequestDto,
  OrganizationApplicationUpdateRequest as UpdateOrganizationApplicationRequestDto,
  PaginatedResponseMicrosoftTenantResponse as PaginatedResponseMicrosoftTenantResponseDto,
  RequestTypeCategoryResponse as RequestTypeCategoryResponseDto,
  SendChangePackageEmailRequest as sendPackageEmailDto,
  UserRoleUpdateRequest as UserRoleUpdateRequestDto,
} from '@electricjs/core_entity-client';
import { ApiListResponseDto } from '@externalTypes/general';

import { apiSlice, TagTypes } from '@/redux/slices/apiSlice';
import {
  transformEmployee,
  transformEmployeeDirectoryListing,
} from '@/redux/slices/employeeApiSlice';
import {
  DeviceMdmActions,
  GetDeviceMdmActionsArgs,
  OrganizationApplication,
  PrivateApplicationUpdateArgs,
} from '@/types/applications';
import { PaginatedResponse } from '@/types/common';
import {
  Employee,
  EmployeeDirectoryListing,
  EmployeeSource,
} from '@/types/employees';
import {
  GetApplicationGroupsArgs,
  GetOrganizationGroupsArgs,
  Group,
} from '@/types/groups';
import {
  AdditionalInfo,
  AddOrganizationEmployees,
  BulkOrganizationApplicationCreationArgs,
  ConfirmOrganizationAbmResellerArgs,
  CreateMicrosoftTenantArgs,
  Entitlement,
  GetOrganizationApplicationChampionsBulkArgs,
  GetOrganizationApplicationChampionsBulkResponse,
  GetOrganizationApplicationCreationArgs,
  GetOrganizationEmployeesArgs,
  GetOrganizationResellersArgs,
  GetOrganizations,
  GetResellerByAbmIdArgs,
  HRISIntegration,
  IntakeForm,
  ArchiveMicrosoftTenantArgs,
  ListMicrosoftTenantsArgs,
  ListOrganizationApplicationChampionsArgs,
  ListOrganizationApplicationChampionsResponse,
  ListOrganizationApplicationsArgs,
  ListOrganizationApplicationsResponse,
  ListRequestTypeCategoryChampionsArgs,
  MicrosoftTenant,
  Organization,
  OrganizationAbmReseller,
  OrganizationAdditionalInfoGetArgs,
  OrganizationAdditionalInfoUpsertArgs,
  WarrantyAutoUpgradeArgs,
  OrganizationAddOn,
  OrganizationApplicationChampionsUpdateArgs,
  OrganizationApplicationCreationArgs,
  OrganizationApplicationDeletionArgs,
  OrganizationApplicationUpdateArgs,
  OrganizationGetGroupArgs,
  OrganizationGroupCreationArgs,
  OrganizationGroupCreationResponse,
  OrganizationGroupDeletionArgs,
  OrganizationIntakeFormCreationArgs,
  OrganizationIntakeFormGetArgs,
  OrganizationPlanManageArgs,
  OrganizationUserRoleUpdateArgs,
  GetOrganizationEmployeeDirectoryArgs,
} from '@/types/organizations';
import { ChampionResponse, RequestTypeCategory } from '@/types/requests';

import {
  invalidatesListAndById,
  providesById,
  providesListAndAllById,
} from './helpers/cacheTagHelpers';
import { transformApplication } from './applicationApiSlice';
import { Address, Country } from '@electricjs/arc';
import { transformGroup } from './groupApiSlice';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

const queryGetOrganizationEmployees = ({
  organizationId,
  limit,
  offset,
  includeDevices,
  term,
  status,
  orderBy,
  sortOrder,
  organizationPackageId,
  xboardingStatus,
  hasAssets,
  hasDevices,
  groupIds,
  startDateFrom,
  startDateTo,
  terminated,
  deviceNotRequired,
}: GetOrganizationEmployeesArgs) => {
  const params = new URLSearchParams();

  params.append('include_devices', String(includeDevices ?? false));
  term && params.append('term', String(term));
  organizationPackageId &&
    params.append('organization_package_id', String(organizationPackageId));
  xboardingStatus && params.append('xboarding_status', String(xboardingStatus));
  orderBy && params.append('order_by', String(orderBy));
  sortOrder && params.append('sort_order', String(sortOrder));
  limit !== undefined && params.append('limit', String(limit));
  offset !== undefined && params.append('offset', String(offset));
  hasAssets !== undefined && params.append('has_assets', String(hasAssets));
  hasDevices !== undefined && params.append('has_devices', String(hasDevices));
  groupIds?.forEach(id => params.append('group_ids', id));
  startDateFrom && params.append('start_date_from', startDateFrom);
  startDateTo && params.append('start_date_to', startDateTo);
  terminated !== undefined && params.append('terminated', String(terminated));
  deviceNotRequired !== undefined &&
    params.append('device_not_required', String(deviceNotRequired));

  if (typeof status === 'string') {
    // when status is a string add a single parameter
    params.append('status', String(status));
  } else {
    // add multiple parameters when status is a list
    status && status.forEach(s => params.append('status', s));
  }

  return {
    url: `/v1/organizations/${organizationId}/employees?${params.toString()}`,
  };
};

const queryGetOrganizationEmployeeDirectory = ({
  organizationId,
  term,
  status,
  orderBy,
  sortOrder,
}: GetOrganizationEmployeeDirectoryArgs) => {
  const params = new URLSearchParams();

  term && params.append('term', String(term));
  orderBy && params.append('order_by', String(orderBy));
  sortOrder && params.append('sort_order', String(sortOrder));

  if (typeof status === 'string') {
    // when status is a string add a single parameter
    params.append('status', String(status));
  } else {
    // add multiple parameters when status is a list
    status && status.forEach(s => params.append('status', s));
  }

  return {
    url: `/v1/organizations/${organizationId}/employees/directory?${params.toString()}`,
  };
};

type OrganizationApplicationDtoWithSop = OrganizationApplicationDto & {
  onboarding_sop_instructions?: string;
  offboarding_sop_instructions?: string;
};

type UpdateOrganizationApplicationRequestDtoWithSop =
  UpdateOrganizationApplicationRequestDto & {
    onboarding_sop_instructions?: string;
    offboarding_sop_instructions?: string;
  };

export const transformOrganizationApplication = (
  organizationApplication: OrganizationApplicationDtoWithSop
): OrganizationApplication => {
  return {
    id: organizationApplication?.id,
    application: transformApplication(organizationApplication?.application),
    organization: organizationApplication?.organization,
    url: organizationApplication?.url,
    userManagementUrl: organizationApplication?.user_management_url,
    onboardingAutomationOn: organizationApplication?.onboarding_automation_on,
    offboardingAutomationOn: organizationApplication?.offboarding_automation_on,
    createdAt: organizationApplication?.created_at,
    updatedAt: organizationApplication?.updated_at,
    integrationConnected: organizationApplication?.integration_connected,
    integrationStatus: organizationApplication?.integration_status,
    disableCreateAccountRequests:
      organizationApplication?.disable_create_account_requests,
    disableDeleteAccountRequests:
      organizationApplication?.disable_delete_account_requests,
    isHrisSource: organizationApplication?.is_hris_source,
    externalOrganizationId: organizationApplication?.external_organization_id,
    onboardingSopInstructions:
      organizationApplication?.onboarding_sop_instructions,
    offboardingSopInstructions:
      organizationApplication?.offboarding_sop_instructions,
    servicePrincipalId: organizationApplication?.metadata?.service_principal_id,
    provisioningJobId: organizationApplication?.metadata?.provisioning_job_id,
  };
};

const mapAdditionalInfoResponse = (
  additionalInfo: OrganizationAdditionalInfoDto
): AdditionalInfo => {
  return {
    organizationId: additionalInfo.organization_id,
    emailProvider: additionalInfo.email_provider,
    itGoal: additionalInfo.it_goal,
    abmStatus: additionalInfo.abm_enrollment_status,
    abmID: additionalInfo.customer_abm_dep_key,
    sendAutomaticWelcomeEmails: additionalInfo.send_automatic_welcome_emails,
    warrantyUpgradePreference: additionalInfo.warranty_upgrade_preference,
    termsRequired: additionalInfo.terms_required,
    termsAccepted: additionalInfo.terms_accepted,
    goLiveDate: additionalInfo.go_live_date,
    scheduledRequestsTimeInAdvance:
      additionalInfo.scheduled_requests_time_in_advance,
    autoGrantAccessOnGroupJoin: additionalInfo.auto_grant_access_on_group_join,
    autoRevokeAccessOnGroupLeave:
      additionalInfo.auto_revoke_access_on_group_leave,
    applicationsAutomationPreference:
      additionalInfo.applications_automation_preference,
    hardwareProvisioningPreference:
      additionalInfo.hardware_provisioning_preference,
    offboardingApplicationsPreference:
      additionalInfo.offboarding_applications_preference,
    offboardingHardwarePreference:
      additionalInfo.offboarding_hardware_preference,
    offboardingRunTime: additionalInfo.offboarding_run_time,
    offboardingTimeZone: additionalInfo.offboarding_time_zone,
    offboardingCanBeImmediate: additionalInfo.offboarding_can_be_immediate,
    offboardingReliesOnEmployeeTimeZone:
      additionalInfo.offboarding_relies_on_employee_time_zone,
    gettingStartedProgress: additionalInfo.getting_started_progress,
  };
};

export const organizationApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    //
    // Organizations
    //
    getOrganizations: builder.query<Organization[], GetOrganizations>({
      query: ({ limit = 10, offset } = {}) =>
        `/v1/organizations?limit=${limit}${offset ? `&offset=${offset}` : ''}`,
      transformResponse: (response: ApiListResponseDto<OrganizationDto>) => {
        return response?.results?.map(org => {
          return {
            id: org?.id,
            name: org?.name,
            salesforceId: org?.salesforce_id,
            hrisIntegration: org?.hris_integrations[0]
              ? transformHRISintegration(org?.hris_integrations[0])
              : undefined,
            entitlements: org?.entitlements[0]
              ? transformEntitlements(org?.entitlements)
              : [],
            internalType: org.internal_type,
          };
        });
      },
      providesTags: providesListAndAllById(TagTypes.Organization),
    }),
    getOrganization: builder.query<Organization, string>({
      query: id => `/v1/organizations/${id}`,
      transformResponse: (organizationDto: OrganizationDto) => {
        return {
          id: organizationDto?.id,
          name: organizationDto?.name,
          salesforceId: organizationDto?.salesforce_id,
          hrisIntegration: organizationDto?.hris_integrations[0]
            ? transformHRISintegration(organizationDto?.hris_integrations[0])
            : undefined,
          addOns: organizationDto?.add_ons
            ? transformAddOns(organizationDto?.add_ons)
            : undefined,
          entitlements: organizationDto?.entitlements[0]
            ? transformEntitlements(organizationDto?.entitlements)
            : [],
          internalType: organizationDto.internal_type,
          organizationPackages: (organizationDto.packages ?? []).map(
            orgPkg => ({
              id: orgPkg.id,
              organizationId: orgPkg.organization_id,
              package: {
                id: orgPkg.package?.id ?? '',
                sku: orgPkg.package?.sku ?? '',
                name: orgPkg.package?.name ?? '',
                displayName: orgPkg.package?.display_name ?? '',
              },
              licenseCount: orgPkg.license_count ?? null,
              availableLicenseCount: orgPkg.available_license_count ?? null,
              // TODO: replace `(orgPkg as any)` with `orgPkg` and remove the eslint-disable
              // once @electricjs/core_entity-client is bumped to include `applications_limit`
              // on OrganizationPackageResponse.
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              applicationsLimit: (orgPkg as any).applications_limit ?? null,
            })
          ),
        };
      },
      providesTags: (_results, _error, id) =>
        providesById(TagTypes.Organization, id),
    }),

    //
    // Organization packages (plans)
    //
    sendPlanInterestEmail: builder.mutation<void, OrganizationPlanManageArgs>({
      query: ({ organizationId, planName }) => {
        const transformedPayload: sendPackageEmailDto = {
          package_name: planName,
        };

        return {
          url: `/v1/organizations/${organizationId}/packages/send_change_interest_email`,
          method: 'POST',
          body: transformedPayload,
        };
      },
    }),

    //
    // Organization employees (without pagination to be shown in lists/dropdowns)
    //
    getOrganizationEmployees: builder.query<
      Employee[],
      GetOrganizationEmployeesArgs
    >({
      query: queryGetOrganizationEmployees,
      providesTags: [TagTypes.Employee],
      transformResponse: (response: ApiListResponseDto<EmployeeDto>) => {
        // Then, the "first page" results will be directly considered the full employee list
        // As some orgs can have thousands of employees, instead of fetching all employees at once to show in the dropdown, we are async fetching while they are typing in and limiting the results to small chunks (e.g., first 20s)
        return response?.results?.map(employee => transformEmployee(employee));
      },
    }),

    //
    // Organization employees total
    //
    getOrganizationEmployeesCount: builder.query<
      number,
      GetOrganizationEmployeesArgs
    >({
      query: args => {
        return queryGetOrganizationEmployees({
          ...args,
          limit: 0,
          offset: 0,
        });
      },
      providesTags: [TagTypes.Employee],
      transformResponse: (response: ApiListResponseDto<EmployeeDto>) => {
        return response.total ?? 0;
      },
    }),

    getOrganizationEmployeeDirectory: builder.query<
      EmployeeDirectoryListing[],
      GetOrganizationEmployeeDirectoryArgs
    >({
      query: queryGetOrganizationEmployeeDirectory,
      providesTags: [TagTypes.EmployeeDirectory],
      transformResponse: (
        response: ApiListResponseDto<EmployeeDirectoryListingDto>
      ) => response?.results?.map(transformEmployeeDirectoryListing) ?? [],
    }),

    //
    // Organization employees with pagination
    //
    getOrganizationEmployeesPaginated: builder.query<
      PaginatedResponse<Employee>,
      GetOrganizationEmployeesArgs
    >({
      query: queryGetOrganizationEmployees,
      providesTags: [TagTypes.Employee],
      transformResponse: (response: ApiListResponseDto<EmployeeDto>) => {
        const organizationEmployees =
          response.results.map(employee => transformEmployee(employee)) ?? [];
        return {
          results: organizationEmployees,
          total: response.total ?? 0,
        };
      },
    }),
    addOrganizationEmployees: builder.mutation<
      {
        addedEmployees: Employee[];
        failedEmployeeEmails: string[];
      },
      AddOrganizationEmployees
    >({
      query: ({ organizationId, employees, createUser = true }) => {
        const transformedPayload: AddEmployeesRequestDto = {
          employees: employees.map(employee => ({
            first_name: employee.firstName,
            last_name: employee.lastName,
            job_title: employee.jobTitle,
            email: employee.email,
            status: employee.status ?? EmployeeStatusEnum.Active,
            source: employee.source ?? EmployeeSource.Manual,
            personal_email: employee.personalEmail,
            start_date: employee.startDate,
            address: transformAddress(employee.address),
            phone_number: employee.phone,
            organization_package_id: employee.organizationPackageId,
            manager_id: employee.managerId,
          })),
        };

        return {
          url: `/v1/organizations/${organizationId}/employees?create_user=${createUser}`,
          method: 'POST',
          body: transformedPayload,
        };
      },
      invalidatesTags: (_result, _error, args) => [
        TagTypes.Employee,
        TagTypes.EmployeeWithDevice,
        ...invalidatesListAndById(
          TagTypes.OrganizationPackage,
          args.organizationId
        ),
      ],
      // We need any here while the response can be either the old or new schema,
      // which is not available in the client yet
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transformResponse: (response: any) => {
        // Check if the response follows the new or old schema
        if (response.added_employees !== undefined) {
          // New response schema
          const addedEmployees =
            response.added_employees?.map(transformEmployee) ?? [];
          const failedEmployeeEmails = response.failed_employee_emails ?? [];
          return { addedEmployees, failedEmployeeEmails };
        } else {
          // Old response schema
          const addedEmployees = response.map(transformEmployee) ?? [];
          return { addedEmployees, failedEmployeeEmails: [] };
        }
      },
    }),

    //
    // Organization users
    //
    updateUserRole: builder.mutation<
      void,
      OrganizationUserRoleUpdateArgs & {
        organizationId: string;
        userId: string;
      }
    >({
      query: ({ organizationId, userId, roleSlug }) => {
        const transformedPayload: UserRoleUpdateRequestDto = {
          role_slug: roleSlug,
        };

        return {
          url: `/v1/organizations/${organizationId}/users/${userId}/role`,
          method: 'PATCH',
          body: transformedPayload,
        };
      },
      invalidatesTags: [TagTypes.Employee, TagTypes.EmployeeWithDevice],
    }),

    //
    // Organization applications
    //
    getOrganizationApplications: builder.query<
      ListOrganizationApplicationsResponse,
      ListOrganizationApplicationsArgs
    >({
      providesTags: [TagTypes.Application],
      query: arg => {
        // The API uses snake_case
        const {
          sortOrder: sort_order,
          orderBy: order_by,
          championEmployeeId: champion_employee_id,
          ...params
        } = arg;
        return {
          url: `/v1/organizations/${arg.organizationId}/organization_applications`,
          params: {
            ...params,
            order_by,
            sort_order,
            champion_employee_id,
          },
        };
      },
      transformResponse: (
        response: ApiListResponseDto<OrganizationApplicationDto>
      ) => {
        const organizationApplications =
          response.results.map(orgApp => {
            return transformOrganizationApplication(orgApp);
          }) ?? [];
        return {
          applications: organizationApplications,
          total: response.total ?? 0,
        };
      },
    }),
    getOrganizationApplication: builder.query<
      OrganizationApplication,
      GetOrganizationApplicationCreationArgs
    >({
      providesTags: [TagTypes.Application],
      query: ({ organizationId, applicationKey }) => ({
        url: `/v1/organizations/${organizationId}/organization_applications/${applicationKey}`,
      }),
      transformResponse: (orgApplication: OrganizationApplicationDto) => {
        return transformOrganizationApplication(orgApplication);
      },
    }),
    addOrganizationApplication: builder.mutation<
      void,
      OrganizationApplicationCreationArgs
    >({
      query: ({ organizationId, application, isHrisSource }) => {
        const transformedPayload: AddOrganizationApplicationRequestDto = {
          application_id: application.applicationId,
          url: application.url?.trim(),
          user_management_url: application.userManagementUrl?.trim(),
          external_organization_id: application.externalOrganizationId,
          is_hris_source: isHrisSource,
        };

        return {
          url: `/v1/organizations/${organizationId}/organization_applications`,
          method: 'POST',
          body: transformedPayload,
        };
      },
      invalidatesTags: [TagTypes.Application],
    }),
    getOrganizationApplicationByOrganizationApplicationId: builder.query<
      OrganizationApplication,
      { organizationApplicationId: string; organizationId: string }
    >({
      queryFn: async (
        { organizationApplicationId, organizationId },
        queryApi,
        _extraOptions,
        _fetchWithBaseQuery
      ): Promise<
        { data: OrganizationApplication } | { error: FetchBaseQueryError }
      > => {
        const response = await queryApi.dispatch(
          organizationApiSlice.endpoints.getOrganizationApplications.initiate({
            organizationId,
            limit: 10000,
          })
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        const { applications } =
          response.data as ListOrganizationApplicationsResponse;

        const orgApp = applications.find(
          orgApp => orgApp.id === organizationApplicationId
        );

        if (!orgApp) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: `OrganizationApplication with id=${organizationApplicationId} not found`,
            } as FetchBaseQueryError,
          };
        }

        return {
          data: orgApp,
        };
      },
    }),
    getOrganizationApplicationDeviceMdmActions: builder.query<
      DeviceMdmActions,
      GetDeviceMdmActionsArgs
    >({
      query: ({ organizationId, applicationKey, deviceId }) =>
        `/v1/organizations/${organizationId}/applications/${applicationKey}/devices/${deviceId}/actions`,
      transformResponse: (response: DeviceMdmActionsResponseDto) => ({
        canLock: response.can_lock,
        canWipe: response.can_wipe,
      }),
    }),
    addOrganizationApplicationsBulk: builder.mutation<
      OrganizationApplication[],
      BulkOrganizationApplicationCreationArgs
    >({
      query: ({ organizationId, applicationIds }) => {
        const transformedPayload: BulkOrganizationApplicationCreationRequestDto =
          {
            applications: applicationIds.map(id => ({ application_id: id })),
          };

        return {
          url: `/v1/organizations/${organizationId}/organization_applications/bulk`,
          method: 'POST',
          body: transformedPayload,
        };
      },
      transformResponse: (
        response: BulkOrganizationApplicationCreationResponseDto
      ) => {
        return response.organization_applications.map(orgApp =>
          transformOrganizationApplication(
            orgApp as OrganizationApplicationDtoWithSop
          )
        );
      },
      invalidatesTags: [TagTypes.Application],
    }),
    updateOrganizationApplication: builder.mutation<
      void,
      OrganizationApplicationUpdateArgs
    >({
      query: ({ organizationId, application, isHrisSource }) => {
        const transformedPayload: UpdateOrganizationApplicationRequestDtoWithSop =
          {
            url: application.url?.trim(),
            user_management_url: application.userManagementUrl?.trim(),
            onboarding_automation_on: application.onboardingAutomationOn,
            offboarding_automation_on: application.offboardingAutomationOn,
            disable_create_account_requests:
              application.disableCreateAccountRequests,
            disable_delete_account_requests:
              application.disableDeleteAccountRequests,
            is_hris_source: isHrisSource,
            onboarding_sop_instructions: application.onboardingSopInstructions,
            offboarding_sop_instructions:
              application.offboardingSopInstructions,
            ...(application.externalOrganizationId !== undefined && {
              external_organization_id: application.externalOrganizationId,
            }),
            ...(application.metadata !== undefined && {
              metadata: application.metadata,
            }),
          };
        return {
          url: `/v1/organizations/${organizationId}/organization_applications/${application.applicationId}`,
          method: 'PUT',
          body: transformedPayload,
        };
      },
      invalidatesTags: [TagTypes.Application],
    }),
    updatePrivateApplication: builder.mutation<
      void,
      PrivateApplicationUpdateArgs
    >({
      query: ({ application, organizationId }) => {
        return {
          url: `/v1/organizations/${organizationId}/applications/${application.applicationId}`,
          method: 'PUT',
          body: {
            name: application.name.trim(),
            icon: application.icon,
          },
        };
      },
      invalidatesTags: [TagTypes.Application],
    }),
    deleteOrganizationApplication: builder.mutation<
      void,
      OrganizationApplicationDeletionArgs
    >({
      query: ({ organizationId, applicationKey }) => {
        return {
          url: `/v1/organizations/${organizationId}/organization_applications/${applicationKey}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: [TagTypes.Application],
    }),

    //
    // Organization Application Champions
    //
    getOrganizationApplicationChampions: builder.query<
      ListOrganizationApplicationChampionsResponse,
      ListOrganizationApplicationChampionsArgs
    >({
      providesTags: (_result, _error, args) => [
        {
          type: TagTypes.OrganizationApplicationChampion,
          id: args.applicationKey,
        },
      ],
      query: ({ organizationId, applicationKey, limit, offset }) => {
        return {
          url: `/v1/organizations/${organizationId}/organization_applications/${applicationKey}/champions`,
          params: {
            limit: limit || 50,
            offset: offset || 0,
          },
        };
      },
      transformResponse: (
        response: ApiListResponseDto<ChampionWithRankDto>
      ) => {
        const organizationApplicationChampions =
          response?.results?.map(champion => {
            return {
              id: champion.id,
              employeeId: champion.employee_id,
              rank: champion.rank,
            };
          }) ?? [];
        return {
          champions: organizationApplicationChampions,
          total: response.total ?? 0,
        };
      },
    }),
    getOrganizationApplicationChampionsBulk: builder.query<
      GetOrganizationApplicationChampionsBulkResponse,
      GetOrganizationApplicationChampionsBulkArgs
    >({
      query: ({ organizationId, applicationKeys }) => ({
        url: `/v1/organizations/${organizationId}/organization_applications/champions/bulk`,
        method: 'POST',
        body: { application_keys: applicationKeys },
      }),
      providesTags: result =>
        result
          ? [
              ...Object.entries(result.organizationApplicationChampions).map(
                ([appKey, _champions]) => ({
                  type: TagTypes.OrganizationApplicationChampion,
                  appKey,
                })
              ),
              { type: TagTypes.OrganizationApplicationChampion, id: 'LIST' },
            ]
          : [{ type: TagTypes.OrganizationApplicationChampion, id: 'LIST' }],
      transformResponse: (response: GetOrgAppChampsBulkResponseDto) => {
        const organizationApplicationChampions = Object.entries(
          response.organization_application_champions
        ).reduce(
          (acc, [key, champions]) => ({
            ...acc,
            [key]: champions.map(champion => ({
              id: champion.id,
              employeeId: champion.employee_id,
              rank: champion.rank,
            })),
          }),
          {}
        );
        return { organizationApplicationChampions };
      },
    }),
    updateOrganizationApplicationChampions: builder.mutation<
      void,
      OrganizationApplicationChampionsUpdateArgs
    >({
      query: ({ organizationId, applicationKey, employeeIds }) => {
        return {
          url: `/v1/organizations/${organizationId}/organization_applications/${applicationKey}/champions`,
          method: 'PUT',
          body: { employee_ids: employeeIds },
        };
      },
      invalidatesTags: (_result, _error, { organizationId }) => [
        TagTypes.OrganizationApplicationChampion,
        {
          type: TagTypes.OrganizationApplicationChampionsWithEmployees,
          id: organizationId,
        },
      ],
    }),

    //
    // Request Type Category Champions
    //
    getRequestTypeCategories: builder.query<
      RequestTypeCategory[],
      ListRequestTypeCategoryChampionsArgs
    >({
      providesTags: (_result, _error, { organizationId }) => [
        { type: TagTypes.RequestTypeCategoryChampion, id: organizationId },
      ],
      query: ({ organizationId }) => {
        return {
          url: `/v1/organizations/${organizationId}/request_type_categories`,
        };
      },
      transformResponse: (
        response: ApiListResponseDto<RequestTypeCategoryResponseDto>
      ): RequestTypeCategory[] => {
        const requestCategoriesWithChampions =
          response?.results.map(requestTypeCategory => {
            const champions: ChampionResponse[] =
              requestTypeCategory.champions?.map((champ, index) => ({
                id: champ.id ?? '',
                employeeId: champ.employee_id ?? '',
                // TODO: Backend does not send rank here, so we derive a stable one
                rank: index,
              })) ?? [];

            return {
              id: requestTypeCategory.id,
              name: requestTypeCategory.name,
              slug: requestTypeCategory.slug,
              description: requestTypeCategory.description,
              champions,
              serviceProviderId: requestTypeCategory.service_provider_id,
              isSupportCenterTile: requestTypeCategory.is_support_center_tile,
            };
          }) ?? [];

        return requestCategoriesWithChampions;
      },
    }),

    //
    // Organization groups
    //
    getOrganizationGroups: builder.query<Group[], GetOrganizationGroupsArgs>({
      query: ({
        organizationId,
        limit,
        offset,
        orderBy,
        sortOrder,
        searchTerm,
      }) => {
        return {
          url: `/v1/organizations/${organizationId}/groups`,
          params: {
            limit: limit || 1000,
            offset: offset || 0,
            sort_order: sortOrder || 'asc',
            order_by: orderBy || 'name',
            search_term: searchTerm,
          },
          meta: {
            entitlements: [EntitlementSlugEnum.EmployeeGroups],
          },
        };
      },
      providesTags: providesListAndAllById(TagTypes.Group),
      transformResponse: (response: ApiListResponseDto<GroupDto>) => {
        return response?.results?.map(group => transformGroup(group));
      },
    }),
    getOrganizationGroupsByApplication: builder.query<
      Group[],
      GetApplicationGroupsArgs
    >({
      query: ({ organizationId, applicationKey }) => {
        return {
          url: `/v1/organizations/${organizationId}/organization_applications/${applicationKey}/groups`,
          meta: {
            entitlements: [EntitlementSlugEnum.EmployeeGroups],
          },
        };
      },
      providesTags: providesListAndAllById(TagTypes.Group),
      transformResponse: (response: GroupDto[]) => {
        return response?.map(group => transformGroup(group));
      },
    }),
    getOrganizationGroupsPaginated: builder.query<
      PaginatedResponse<Group>,
      GetOrganizationGroupsArgs
    >({
      query: ({
        organizationId,
        limit,
        offset,
        orderBy,
        sortOrder,
        searchTerm,
      }) => {
        return {
          url: `/v1/organizations/${organizationId}/groups`,
          params: {
            limit: limit || 1000,
            offset: offset || 0,
            sort_order: sortOrder || 'asc',
            order_by: orderBy || 'name',
            search_term: searchTerm,
          },
          meta: {
            entitlements: [EntitlementSlugEnum.EmployeeGroups],
          },
        };
      },
      providesTags: [TagTypes.Group],
      transformResponse: (response: ApiListResponseDto<GroupDto>) => {
        const organizationGroups =
          response.results.map(group => transformGroup(group)) ?? [];
        return {
          results: organizationGroups,
          total: response.total ?? 0,
        };
      },
    }),
    createOrganizationGroup: builder.mutation<
      OrganizationGroupCreationResponse,
      OrganizationGroupCreationArgs
    >({
      query: ({
        organizationId,
        name,
        employeeIds,
        parentGroupId,
        membershipType,
        ruleGroups,
      }) => {
        const body: AddGroupRequestDto = {
          name,
          employee_ids: employeeIds,
        };

        if (parentGroupId) {
          body.parent_group_id = parentGroupId;
        }

        if (membershipType) {
          body.membership_type = membershipType;
        }

        if (ruleGroups) {
          body.rule_groups = ruleGroups;
        }

        return {
          url: `/v1/organizations/${organizationId}/groups`,
          method: 'POST',
          body,
          meta: {
            entitlements: [EntitlementSlugEnum.EmployeeGroups],
          },
        };
      },
      invalidatesTags: [TagTypes.Group],
    }),
    getOrganizationGroup: builder.query<Group, OrganizationGetGroupArgs>({
      query: ({ organizationId, groupId }) => {
        return {
          url: `/v1/organizations/${organizationId}/groups/${groupId}`,
          params: {
            include_devices: true,
          },
          meta: {
            entitlements: [EntitlementSlugEnum.EmployeeGroups],
          },
        };
      },
      providesTags: (_result, _error, args) =>
        providesById(TagTypes.Group, args.groupId),
      transformResponse: (group: GroupDto) => {
        return transformGroup(group);
      },
    }),
    deleteOrganizationGroup: builder.mutation<
      void,
      OrganizationGroupDeletionArgs
    >({
      query: ({ organizationId, groupId }) => {
        return {
          url: `/v1/organizations/${organizationId}/groups/${groupId}`,
          method: 'DELETE',
          meta: {
            entitlements: [EntitlementSlugEnum.EmployeeGroups],
          },
        };
      },
      invalidatesTags: (_results, _error, args) =>
        invalidatesListAndById(TagTypes.Group, args.groupId),
    }),
    createOrganizationIntakeForm: builder.mutation<
      IntakeForm,
      OrganizationIntakeFormCreationArgs
    >({
      query: ({ organizationId, answers }) => {
        return {
          url: `/v1/organizations/${organizationId}/intake_form`,
          method: 'POST',
          body: {
            answers,
          },
          meta: {
            entitlements: [EntitlementSlugEnum.IntakeForm],
          },
        };
      },
      invalidatesTags: (_results, _error, args) =>
        invalidatesListAndById(TagTypes.IntakeForm, args.organizationId),
    }),
    getOrganizationIntakeForm: builder.query<
      IntakeForm,
      OrganizationIntakeFormGetArgs
    >({
      query: ({ organizationId }) => {
        return {
          url: `/v1/organizations/${organizationId}/intake_form`,
          meta: {
            entitlements: [EntitlementSlugEnum.IntakeForm],
          },
        };
      },
      providesTags: (_result, _error, args) =>
        providesById(TagTypes.IntakeForm, args.organizationId),
      transformResponse: (intakeForm: IntakeFormDto) => {
        return {
          id: intakeForm.id,
          organizationId: intakeForm.organization_id,
          employeeId: intakeForm.employee_id,
          answers: { ...intakeForm.answers },
          createdAt: intakeForm.created_at,
        };
      },
    }),
    upsertOrganizationAdditionalInfo: builder.mutation<
      AdditionalInfo,
      OrganizationAdditionalInfoUpsertArgs
    >({
      query: ({
        organizationId,
        emailProvider,
        itGoal,
        scheduledRequestsTimeInAdvance,
        applicationsAutomationPreference,
        hardwareProvisioningPreference,
      }) => {
        return {
          url: `/v1/organizations/${organizationId}/additional_info`,
          method: 'PUT',
          body: {
            email_provider: emailProvider,
            it_goal: itGoal,
            ...(scheduledRequestsTimeInAdvance !== undefined && {
              scheduled_requests_time_in_advance:
                scheduledRequestsTimeInAdvance,
            }),
            ...(applicationsAutomationPreference !== undefined && {
              applications_automation_preference:
                applicationsAutomationPreference,
            }),
            ...(hardwareProvisioningPreference !== undefined && {
              hardware_provisioning_preference: hardwareProvisioningPreference,
            }),
          },
        };
      },
      invalidatesTags: (_results, _error, args) =>
        invalidatesListAndById(TagTypes.AdditionalInfo, args.organizationId),
      transformResponse: mapAdditionalInfoResponse,
    }),
    updateGroupChangeAutomationSettings: builder.mutation<
      AdditionalInfo,
      Pick<
        OrganizationAdditionalInfoUpsertArgs,
        | 'organizationId'
        | 'autoGrantAccessOnGroupJoin'
        | 'autoRevokeAccessOnGroupLeave'
      >
    >({
      query: ({
        organizationId,
        autoGrantAccessOnGroupJoin,
        autoRevokeAccessOnGroupLeave,
      }) => {
        return {
          url: `/v1/organizations/${organizationId}/additional_info`,
          method: 'PUT',
          body: {
            ...(autoGrantAccessOnGroupJoin !== undefined && {
              auto_grant_access_on_group_join: autoGrantAccessOnGroupJoin,
            }),
            ...(autoRevokeAccessOnGroupLeave !== undefined && {
              auto_revoke_access_on_group_leave: autoRevokeAccessOnGroupLeave,
            }),
          },
        };
      },
      invalidatesTags: (_results, _error, args) =>
        invalidatesListAndById(TagTypes.AdditionalInfo, args.organizationId),
      transformResponse: mapAdditionalInfoResponse,
    }),
    // Single PUT that persists every Employee Updates preference (IT
    // onboarding + group transfer) in one request.
    updateEmployeeUpdatesSettings: builder.mutation<
      AdditionalInfo,
      Pick<
        OrganizationAdditionalInfoUpsertArgs,
        | 'organizationId'
        | 'applicationsAutomationPreference'
        | 'hardwareProvisioningPreference'
        | 'scheduledRequestsTimeInAdvance'
        | 'autoGrantAccessOnGroupJoin'
        | 'autoRevokeAccessOnGroupLeave'
        | 'offboardingApplicationsPreference'
        | 'offboardingHardwarePreference'
        | 'offboardingRunTime'
        | 'offboardingTimeZone'
        | 'offboardingCanBeImmediate'
        | 'offboardingReliesOnEmployeeTimeZone'
      >
    >({
      query: ({
        organizationId,
        applicationsAutomationPreference,
        hardwareProvisioningPreference,
        scheduledRequestsTimeInAdvance,
        autoGrantAccessOnGroupJoin,
        autoRevokeAccessOnGroupLeave,
        offboardingApplicationsPreference,
        offboardingHardwarePreference,
        offboardingRunTime,
        offboardingTimeZone,
        offboardingCanBeImmediate,
        offboardingReliesOnEmployeeTimeZone,
      }) => {
        return {
          url: `/v1/organizations/${organizationId}/additional_info`,
          method: 'PUT',
          body: {
            ...(applicationsAutomationPreference !== undefined && {
              applications_automation_preference:
                applicationsAutomationPreference,
            }),
            ...(hardwareProvisioningPreference !== undefined && {
              hardware_provisioning_preference: hardwareProvisioningPreference,
            }),
            ...(scheduledRequestsTimeInAdvance !== undefined && {
              scheduled_requests_time_in_advance:
                scheduledRequestsTimeInAdvance,
            }),
            ...(autoGrantAccessOnGroupJoin !== undefined && {
              auto_grant_access_on_group_join: autoGrantAccessOnGroupJoin,
            }),
            ...(autoRevokeAccessOnGroupLeave !== undefined && {
              auto_revoke_access_on_group_leave: autoRevokeAccessOnGroupLeave,
            }),
            ...(offboardingApplicationsPreference !== undefined && {
              offboarding_applications_preference:
                offboardingApplicationsPreference,
            }),
            ...(offboardingHardwarePreference !== undefined && {
              offboarding_hardware_preference: offboardingHardwarePreference,
            }),
            ...(offboardingRunTime !== undefined && {
              offboarding_run_time: offboardingRunTime,
            }),
            ...(offboardingTimeZone !== undefined && {
              offboarding_time_zone: offboardingTimeZone,
            }),
            ...(offboardingCanBeImmediate !== undefined && {
              offboarding_can_be_immediate: offboardingCanBeImmediate,
            }),
            ...(offboardingReliesOnEmployeeTimeZone !== undefined && {
              offboarding_relies_on_employee_time_zone:
                offboardingReliesOnEmployeeTimeZone,
            }),
          },
        };
      },
      invalidatesTags: (_results, _error, args) =>
        invalidatesListAndById(TagTypes.AdditionalInfo, args.organizationId),
      transformResponse: mapAdditionalInfoResponse,
    }),
    updateABMOrganizationInfo: builder.mutation<
      AdditionalInfo,
      OrganizationAdditionalInfoUpsertArgs
    >({
      query: ({ organizationId, abmID, abmStatus }) => {
        return {
          url: `/v1/organizations/${organizationId}/additional_info`,
          method: 'PUT',
          body: {
            abm_enrollment_status: abmStatus,
            customer_abm_dep_key: abmID,
          },
        };
      },
      invalidatesTags: (_results, _error, args) =>
        invalidatesListAndById(TagTypes.AdditionalInfo, args.organizationId),
      transformResponse: mapAdditionalInfoResponse,
    }),
    getOrganizationAdditionalInfo: builder.query<
      AdditionalInfo,
      OrganizationAdditionalInfoGetArgs
    >({
      query: ({ organizationId }) => {
        return {
          url: `/v1/organizations/${organizationId}/additional_info`,
        };
      },
      providesTags: (_result, _error, args) =>
        providesById(TagTypes.AdditionalInfo, args.organizationId),
      transformResponse: mapAdditionalInfoResponse,
    }),
    updateGettingStartedProgress: builder.mutation<
      AdditionalInfo,
      OrganizationAdditionalInfoUpsertArgs
    >({
      query: ({ organizationId, gettingStartedProgress }) => {
        return {
          url: `/v1/organizations/${organizationId}/additional_info`,
          method: 'PUT',
          body: {
            getting_started_progress: gettingStartedProgress,
          },
        };
      },
      // Optimistic patch for instant checkbox feedback. invalidatesTags below
      // refetches the canonical AdditionalInfo on both success and failure
      // (RTK Query's default), which reconciles the cache with server truth —
      // including undoing this patch on failure — without a manual undo() that
      // could clobber a different, concurrently-succeeded update.
      onQueryStarted: async (
        { organizationId, gettingStartedProgress },
        { dispatch, queryFulfilled }
      ) => {
        dispatch(
          organizationApiSlice.util.updateQueryData(
            'getOrganizationAdditionalInfo',
            { organizationId },
            draft => {
              draft.gettingStartedProgress ??= {};
              Object.assign(
                draft.gettingStartedProgress,
                gettingStartedProgress
              );
            }
          )
        );
        // Prevent an unhandled rejection; invalidatesTags (which runs
        // regardless of success/failure) is what reconciles the cache.
        queryFulfilled.catch(() => {});
      },
      invalidatesTags: (_results, _error, args) =>
        invalidatesListAndById(TagTypes.AdditionalInfo, args.organizationId),
      transformResponse: mapAdditionalInfoResponse,
    }),
    updateWarrantyAutoUpgrade: builder.mutation<
      AdditionalInfo,
      WarrantyAutoUpgradeArgs
    >({
      query: ({ organizationId, warrantyAutoUpgrade }) => {
        return {
          url: `/v1/organizations/${organizationId}/additional_info`,
          method: 'PUT',
          body: {
            warranty_upgrade_preference: warrantyAutoUpgrade,
          },
        };
      },
      invalidatesTags: (_results, _error, args) =>
        invalidatesListAndById(TagTypes.AdditionalInfo, args.organizationId),
      transformResponse: mapAdditionalInfoResponse,
    }),
    getWorkflowStatus: builder.query<
      { status: string },
      { organizationId: string; workflowId: string }
    >({
      query: ({ organizationId, workflowId }) => ({
        url: `/v2/organizations/${organizationId}/workflows/${workflowId}/status`,
        method: 'GET',
      }),
      transformResponse: (response: {
        workflow_id: string;
        status: string;
      }): { status: string } => ({
        status: response.status,
      }),
    }),
    acceptTerms: builder.mutation<void, { organizationId: string }>({
      query: ({ organizationId }) => ({
        url: `/v1/organizations/${organizationId}/additional_info`,
        method: 'PUT',
        body: { terms_accepted: true },
      }),
      invalidatesTags: (_results, _error, args) =>
        invalidatesListAndById(TagTypes.AdditionalInfo, args.organizationId),
    }),

    //
    // Microsoft Tenants
    //
    listMicrosoftTenants: builder.query<
      PaginatedResponse<MicrosoftTenant>,
      ListMicrosoftTenantsArgs
    >({
      query: ({ organizationId, limit, offset, orderBy, sortOrder }) => ({
        url: `/v1/organizations/${organizationId}/microsoft_tenants`,
        params: {
          limit: limit || 5,
          offset: offset || 0,
          sort_order: sortOrder || 'asc',
          order_by: orderBy || 'name',
        },
      }),
      providesTags: [{ type: TagTypes.MicrosoftTenant, id: 'LIST' }],
      transformResponse: (
        response: PaginatedResponseMicrosoftTenantResponseDto
      ): PaginatedResponse<MicrosoftTenant> => ({
        total: response.total,
        results: response.results.map(tenant => ({
          id: tenant.id,
          organizationId: tenant.organization_id,
          name: tenant.name,
          msTenantId: tenant.ms_tenant_id,
          domain: tenant.domain,
          createdAt: tenant.created_at,
          updatedAt: tenant.updated_at,
        })),
      }),
    }),
    createMicrosoftTenant: builder.mutation<
      MicrosoftTenant,
      CreateMicrosoftTenantArgs
    >({
      query: ({ organizationId, name, msTenantId, domain }) => {
        const body: NewMicrosoftTenantRequestDto = {
          name,
          ms_tenant_id: msTenantId,
          domain,
        };

        return {
          url: `/v1/organizations/${organizationId}/microsoft_tenants`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: [{ type: TagTypes.MicrosoftTenant, id: 'LIST' }],
      transformResponse: (
        response: MicrosoftTenantResponseDto
      ): MicrosoftTenant => ({
        id: response.id,
        organizationId: response.organization_id,
        name: response.name,
        msTenantId: response.ms_tenant_id,
        domain: response.domain,
        createdAt: response.created_at,
        updatedAt: response.updated_at,
      }),
    }),
    archiveMicrosoftTenant: builder.mutation<void, ArchiveMicrosoftTenantArgs>({
      query: ({ organizationId, msTenantInternalId }) => ({
        url: `/v1/organizations/${organizationId}/microsoft_tenants/${msTenantInternalId}/archive`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: TagTypes.MicrosoftTenant, id: 'LIST' }],
    }),

    //
    // Organization ABM resellers
    //
    getOrganizationResellers: builder.query<
      OrganizationAbmReseller[],
      GetOrganizationResellersArgs
    >({
      query: ({ organizationId }) => ({
        url: `/v2/organizations/${organizationId}/resellers`,
      }),
      providesTags: (_result, _error, args) =>
        providesById(TagTypes.OrganizationReseller, args.organizationId),
      transformResponse: (response: OrganizationAbmResellerDto[]) =>
        response.map(transformOrganizationReseller),
    }),
    confirmOrganizationReseller: builder.mutation<
      OrganizationAbmReseller[],
      ConfirmOrganizationAbmResellerArgs
    >({
      query: ({ organizationId, resellerIds }) => ({
        url: `/v2/organizations/${organizationId}/resellers`,
        method: 'POST',
        body: { reseller_ids: resellerIds },
      }),
      invalidatesTags: (_result, _error, args) =>
        invalidatesListAndById(
          TagTypes.OrganizationReseller,
          args.organizationId
        ),
      transformResponse: (response: OrganizationAbmResellerDto[]) =>
        response.map(transformOrganizationReseller),
    }),
    getResellerByAbmId: builder.query<
      OrganizationAbmReseller,
      GetResellerByAbmIdArgs
    >({
      query: ({ organizationId, abmResellerId, countryCode }) => ({
        url: `/v2/organizations/${organizationId}/resellers/lookup`,
        params: {
          abm_reseller_id: abmResellerId,
          country_code: countryCode,
        },
      }),
      transformResponse: (response: OrganizationAbmResellerDto) =>
        transformOrganizationReseller(response),
    }),
  }),
});

type OrganizationAbmResellerDto = {
  id: string;
  abm_reseller_id: string;
  country_code: string;
};

const transformOrganizationReseller = (
  reseller: OrganizationAbmResellerDto
): OrganizationAbmReseller => ({
  id: reseller.id,
  abmResellerId: reseller.abm_reseller_id,
  countryCode: reseller.country_code as Country,
});

export const transformHRISintegration = (
  integration: HRISIntegrationDto
): HRISIntegration => {
  return {
    id: integration.id,
    hrisProvider: integration.hris_provider,
    lastSync: integration.last_fetched_at,
    createdBy: integration.created_by,
  };
};

export const transformAddOns = (
  organizationAddOns: OrganizationAddOnDto[]
): OrganizationAddOn[] => {
  return organizationAddOns.map(organizationAddOn => {
    return {
      id: organizationAddOn.add_on_id,
      name: organizationAddOn.name,
      vendor: organizationAddOn.vendor,
      licenseCount: organizationAddOn.license_count,
      organizationId: organizationAddOn.organization_id,
    };
  });
};

export const transformEntitlements = (
  entitlements: EntitlementDto[]
): Entitlement[] => {
  return entitlements.map(entitlement => {
    return {
      id: entitlement.id,
      name: entitlement.name,
      slug: entitlement.slug,
    };
  });
};

const transformAddress = (address: Address | undefined) => {
  if (!address || !Object.values(address).some(value => value)) {
    return undefined;
  }

  return {
    street_1: address.streetAddress1,
    street_2: address.streetAddress2 ?? undefined,
    city: address.city,
    country: address.country,
    state: address.state,
    zip_code: address.zip,
  };
};

// export auto-generated hooks
export const {
  useAddOrganizationApplicationMutation,
  useAddOrganizationApplicationsBulkMutation,
  useAddOrganizationEmployeesMutation,
  useCreateOrganizationGroupMutation,
  useDeleteOrganizationApplicationMutation,
  useDeleteOrganizationGroupMutation,
  useGetOrganizationApplicationQuery,
  useLazyGetOrganizationApplicationQuery,
  useGetOrganizationApplicationByOrganizationApplicationIdQuery,
  useGetOrganizationApplicationDeviceMdmActionsQuery,
  useGetOrganizationApplicationsQuery,
  useGetOrganizationGroupsByApplicationQuery,
  useLazyGetOrganizationGroupsByApplicationQuery,
  useGetRequestTypeCategoriesQuery,
  useLazyGetRequestTypeCategoriesQuery,
  useLazyGetOrganizationApplicationsQuery,
  useGetOrganizationApplicationChampionsQuery,
  useLazyGetOrganizationApplicationChampionsQuery,
  useGetOrganizationApplicationChampionsBulkQuery,
  useUpdateOrganizationApplicationChampionsMutation,
  useGetOrganizationEmployeesQuery,
  useGetOrganizationEmployeesCountQuery,
  useGetOrganizationEmployeesPaginatedQuery,
  useLazyGetOrganizationEmployeesPaginatedQuery,
  useLazyGetOrganizationEmployeesQuery,
  useGetOrganizationEmployeeDirectoryQuery,
  useLazyGetOrganizationEmployeeDirectoryQuery,
  useGetOrganizationGroupQuery,
  useUpdateOrganizationApplicationMutation,
  useUpdatePrivateApplicationMutation,
  useLazyGetOrganizationGroupQuery,
  useGetOrganizationGroupsQuery,
  useLazyGetOrganizationGroupsPaginatedQuery,
  useLazyGetOrganizationGroupsQuery,
  useLazyGetOrganizationQuery,
  useGetOrganizationQuery,
  useGetOrganizationsQuery,
  useLazyGetOrganizationsQuery,
  useUpdateUserRoleMutation,
  useSendPlanInterestEmailMutation,
  useCreateOrganizationIntakeFormMutation,
  useGetOrganizationIntakeFormQuery,
  useUpsertOrganizationAdditionalInfoMutation,
  useGetOrganizationAdditionalInfoQuery,
  useLazyGetOrganizationAdditionalInfoQuery,
  useUpdateABMOrganizationInfoMutation,
  useUpdateGroupChangeAutomationSettingsMutation,
  useUpdateEmployeeUpdatesSettingsMutation,
  useUpdateGettingStartedProgressMutation,
  useUpdateWarrantyAutoUpgradeMutation,
  useGetWorkflowStatusQuery,
  useAcceptTermsMutation,
  useListMicrosoftTenantsQuery,
  useLazyListMicrosoftTenantsQuery,
  useCreateMicrosoftTenantMutation,
  useArchiveMicrosoftTenantMutation,
  useGetOrganizationResellersQuery,
  useConfirmOrganizationResellerMutation,
  useGetResellerByAbmIdQuery,
  useLazyGetResellerByAbmIdQuery,
} = organizationApiSlice;
